import React, { createContext, useContext, useMemo, useState } from 'react'
import { IPlayerContextType } from './type'

const PlayerContext = createContext<IPlayerContextType | undefined>(undefined)

export const PlayerProvider: React.FC<{
  children: React.ReactNode
  isMiniSeries?: boolean
  isBigBrother?: boolean
  adsInterval?: number
  startMuted?: boolean
  /** a component other than the vertical feed (ad targeting and analytics) */
  componentType?: IPlayerContextType['componentType']
  /** the user pressed the sound button (not a mute forced by the browser) */
  onMuteToggle?: (muted: boolean) => void
  /**
   * A provider kept mounted between viewings: each new value starts a viewing
   * on `startMuted` again, so a mute the browser forced on one does not carry
   */
  session?: number
}> = ({
  children,
  isMiniSeries,
  isBigBrother,
  adsInterval,
  startMuted = false,
  componentType: componentTypeOverride,
  onMuteToggle,
  session,
}) => {
  // the user's own choice
  const [userMuted, setUserMuted] = useState(startMuted)
  // the browser refused sound for the clip on screen: it is tried again by the
  // next clip, and never counts as the user's choice
  const [browserMuted, setBrowserMuted] = useState(false)
  const [lastSession, setLastSession] = useState(session)
  if (session !== lastSession) {
    setLastSession(session)
    setUserMuted(startMuted)
    setBrowserMuted(false)
  }
  const muteForced = browserMuted && !userMuted
  // what is shown and heard
  const isMuted = userMuted || muteForced

  const componentType = useMemo(
    () =>
      componentTypeOverride ??
      (isBigBrother ? 'bigbrother' : isMiniSeries ? 'mini_series' : 'mintly'),
    [componentTypeOverride, isBigBrother, isMiniSeries]
  )
  // a press of the user: it also ends a mute the browser forced
  const choose = (muted: boolean) => {
    onMuteToggle?.(muted)
    setUserMuted(muted)
    setBrowserMuted(false)
  }
  const toggleMute = () => choose(!isMuted)
  // a press that leaves the shared sound as it is: still the user's press
  const reportMute = choose

  const resetMute = () => {
    setUserMuted(true)
  }
  const forceMute = () => {
    setBrowserMuted(true)
  }
  const releaseMute = () => {
    setBrowserMuted(false)
  }

  const resolvedAdsInterval = adsInterval ?? (isMiniSeries ? 1000 : 3)

  return (
    <PlayerContext.Provider
      value={{
        isMuted,
        muteForced,
        toggleMute,
        reportMute,
        resetMute,
        forceMute,
        releaseMute,
        componentType,
        isMiniSeries: !!isMiniSeries,
        isBigBrother: !!isBigBrother,
        adsInterval: resolvedAdsInterval,
      }}
    >
      {children}
    </PlayerContext.Provider>
  )
}

export const usePlayer = () => {
  const context = useContext(PlayerContext)
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider')
  }
  return context
}
